import React, { useState } from 'react';
import { FileUp, X, AlertCircle, CheckCircle, FileText, Loader } from 'lucide-react';
import { extractBuildingFromPDF } from '../utils/pdfImport';
import { Building } from '../types/building';

interface PDFImportDialogProps {
  onClose: () => void;
  onImport: (building: Building) => void;
}

export const PDFImportDialog: React.FC<PDFImportDialogProps> = ({ onClose, onImport }) => {
  const [file, setFile] = useState<File | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [building, setBuilding] = useState<Building | null>(null);
  
  const handleFileChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = e.target.files?.[0];
    if (selectedFile) {
      if (selectedFile.type !== 'application/pdf') {
        setError('Bitte wählen Sie eine PDF-Datei aus.');
        setFile(null);
        return;
      }
      
      setFile(selectedFile);
      setError(null);
    }
  };
  
  const handleDrop = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
    
    if (e.dataTransfer.files && e.dataTransfer.files.length > 0) {
      const droppedFile = e.dataTransfer.files[0];
      
      if (droppedFile.type !== 'application/pdf') {
        setError('Bitte wählen Sie eine PDF-Datei aus.');
        return;
      }
      
      setFile(droppedFile);
      setError(null);
    }
  };
  
  const handleDragOver = (e: React.DragEvent<HTMLDivElement>) => {
    e.preventDefault();
    e.stopPropagation();
  };
  
  const handleAnalyze = async () => {
    if (!file) {
      setError('Bitte wählen Sie eine PDF-Datei aus.');
      return;
    }
    
    setLoading(true);
    setError(null);
    
    try {
      const extractedBuilding = await extractBuildingFromPDF(file);
      setBuilding(extractedBuilding);
    } catch (err) {
      console.error('Error extracting building from PDF:', err);
      setError(err instanceof Error ? err.message : 'Fehler beim Analysieren der PDF');
      setBuilding(null);
    } finally {
      setLoading(false);
    }
  };
  
  const handleImport = () => {
    if (building) {
      onImport(building);
    }
  };
  
  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center p-4 z-50">
      <div className="bg-white rounded-lg p-6 w-full max-w-md">
        <div className="flex justify-between items-center mb-6">
          <h2 className="text-xl font-bold flex items-center gap-2">
            <FileUp className="w-6 h-6 text-blue-600" />
            PDF importieren
          </h2>
          <button 
            onClick={onClose}
            className="text-gray-500 hover:text-gray-700 transition-colors"
          >
            <X className="w-6 h-6" />
          </button>
        </div>
        
        <div className="space-y-6">
          {!building ? (
            <>
              <p className="text-gray-600">
                Wählen Sie eine PDF-Datei aus, die mit InstallTrack Pro erstellt wurde, um die Gebäudestruktur zu importieren.
              </p>
              
              <div 
                className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center"
                onDrop={handleDrop}
                onDragOver={handleDragOver}
              >
                <FileText className="w-12 h-12 text-gray-400 mx-auto mb-4" />
                <p className="text-sm text-gray-500 mb-4">
                  PDF-Datei hier ablegen oder
                </p>
                <label className="inline-flex items-center px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 cursor-pointer">
                  <span>Datei auswählen</span>
                  <input 
                    type="file" 
                    accept=".pdf" 
                    className="hidden" 
                    onChange={handleFileChange}
                  />
                </label>
                {file && (
                  <p className="mt-4 text-sm text-gray-600">
                    Ausgewählte Datei: {file.name}
                  </p>
                )}
              </div>
              
              {error && (
                <div className="bg-red-50 text-red-700 p-3 rounded-md flex items-start">
                  <AlertCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                  <p className="text-sm">{error}</p>
                </div>
              )}
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleAnalyze}
                  disabled={!file || loading}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700 disabled:bg-blue-300 disabled:cursor-not-allowed"
                >
                  {loading ? (
                    <>
                      <Loader className="w-4 h-4 animate-spin" />
                      <span>Analysiere...</span>
                    </>
                  ) : (
                    <span>PDF analysieren</span>
                  )}
                </button>
              </div>
            </>
          ) : (
            <>
              <div className="bg-green-50 text-green-700 p-4 rounded-md flex items-start">
                <CheckCircle className="w-5 h-5 mr-2 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-medium">PDF erfolgreich analysiert</p>
                  <p className="text-sm mt-1">Die folgenden Daten wurden extrahiert:</p>
                </div>
              </div>
              
              <div className="bg-gray-50 p-4 rounded-md">
                <h3 className="font-bold text-gray-800 mb-2">{building.name}</h3>
                <p className="text-gray-600 mb-4">{building.address}</p>
                
                <div className="space-y-2">
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Etagen:</span> {building.floors.length}
                  </p>
                  <p className="text-sm text-gray-700">
                    <span className="font-medium">Wohneinheiten:</span> {building.floors.reduce((sum, floor) => sum + floor.apartments.length, 0)}
                  </p>
                </div>
              </div>
              
              <div className="flex justify-end gap-3">
                <button
                  onClick={onClose}
                  className="px-4 py-2 text-gray-600 hover:bg-gray-100 rounded-md"
                >
                  Abbrechen
                </button>
                <button
                  onClick={handleImport}
                  className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
                >
                  <span>Gebäude importieren</span>
                </button>
              </div>
            </>
          )}
        </div>
      </div>
    </div>
  );
};